import { Input } from '@components'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DropdownWrapper,
  Header,
  HeaderIcon,
  Item,
  ItemColumn,
  ScrollableContent,
  StyledBusinessDropdown
} from './BusinessSearchDropdown.styled'
import { useFormContext } from 'react-hook-form'
import { IEInvoiceAddress } from '@query'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'framer-motion'
import { autoUpdate, flip, offset, size, useDismiss, useFloating } from '@floating-ui/react'

interface Props {
  addressResults: IEInvoiceAddress[]
  onSelect: (result: IEInvoiceAddress) => void
  getOperatorName: (code: string) => string
}

const EInvoiceAddressDropdown: React.FC<Props> = ({
  addressResults,
  onSelect,
  getOperatorName
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState(-1)
  const { t } = useTranslation()
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map())

  const {
    register,
    formState: { errors }
  } = useFormContext()

  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))

  const hasMultiple = addressResults.length > 1

  const handleClose = () => {
    setIsOpen(false)
    setFocusIndex(-1)
  }

  const handleSelect = (result: IEInvoiceAddress) => {
    onSelect(result)
    handleClose()
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (!hasMultiple || !isOpen) return

    const len = addressResults.length
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        const next = focusIndex >= len - 1 ? 0 : focusIndex + 1
        setFocusIndex(next)
        itemRefs.current.get(next)?.scrollIntoView({ block: 'nearest' })
        break
      }

      case 'ArrowUp': {
        e.preventDefault()
        const prev = focusIndex <= 0 ? len - 1 : focusIndex - 1
        setFocusIndex(prev)
        itemRefs.current.get(prev)?.scrollIntoView({ block: 'nearest' })
        break
      }

      case 'Enter': {
        const result = addressResults[focusIndex]
        if (result) {
          handleSelect(result)
          e.stopPropagation()
          e.preventDefault()
        }
        break
      }

      case 'Escape': {
        handleClose()
        break
      }
    }
  }

  const { context, x, y, refs, strategy } = useFloating({
    open: isOpen,
    onOpenChange: open => {
      if (!open) handleClose()
    },
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    middleware: [
      offset(0),
      flip(),
      size({
        apply: ({ availableWidth, availableHeight, elements }) => {
          Object.assign(elements.floating.style, {
            width: `${Math.min(500, availableWidth - 48)}px`,
            maxHeight: `${Math.min(300, availableHeight - 48)}px`
          })
        }
      })
    ]
  })

  useDismiss(context)

  const registered = register('invoicing_einvoice_address')

  return (
    <DropdownWrapper ref={refs.setReference}>
      <Input
        autoComplete="off"
        label={t('contacts.form.einvoiceAddress')}
        info={t('contacts.form.einvoiceAddressInfo')}
        errors={errors?.invoicing_einvoice_address}
        onKeyDown={onKeyDown}
        {...registered}
        onFocus={() => {
          if (hasMultiple) setIsOpen(true)
        }}
        onBlur={e => {
          registered.onBlur?.(e)
          handleClose()
        }}
      />

      {rootRef.current &&
        createPortal(
          <AnimatePresence>
            {isOpen && hasMultiple && (
              <StyledBusinessDropdown
                ref={refs.setFloating}
                style={{
                  top: y || 0,
                  left: x || 0,
                  position: strategy
                }}
              >
                <Header>
                  {t('contacts.form.einvoiceAddressSelect')}
                  <HeaderIcon />
                </Header>

                <ScrollableContent>
                  {addressResults.map((result, i) => (
                    <Item
                      key={`${result.einvoice_address}-${result.operator_code}`}
                      ref={el => {
                        itemRefs.current.set(i, el)
                      }}
                      className={focusIndex === i ? 'keyboard-focused' : undefined}
                      onTouchStart={() => handleSelect(result)}
                      onMouseDown={() => handleSelect(result)}
                    >
                      <ItemColumn className="description">{result.einvoice_address}</ItemColumn>
                      <ItemColumn>{getOperatorName(result.operator_code)}</ItemColumn>
                    </Item>
                  ))}
                </ScrollableContent>
              </StyledBusinessDropdown>
            )}
          </AnimatePresence>,
          rootRef.current
        )}
    </DropdownWrapper>
  )
}

export default EInvoiceAddressDropdown
